/**
 * ContentCalendarGrid — mode-agnostic weeks × 7-days CSS grid.
 *
 * Input shape deliberately does NOT reference CalendarMode. Both Mode A
 * (single strategy full-span) and Mode B (cross-plan window) use this
 * component — the caller picks the window and chip detail level.
 *
 * Layout: `grid-template-columns: auto repeat(7, 1fr)`. The first column
 * is the week label (row header); the next seven are Mon..Sun.
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import type { ContentPublication } from '../../types';
import { buildWeeks, dayKey, daysOfWeek, type CalendarWeek } from '../../utils/calendarDates';
import { DayCell, type DayCellDropPayload, type DayCellTopicDropPayload } from './DayCell';
import type { ChipDetailLevel } from './PublicationChip';

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export interface ContentCalendarGridProps {
  windowStart: Date;
  weekCount: number;
  publications: ContentPublication[];
  colorsByStrategy: Record<string, string>;
  topicTitlesByPub?: Record<string, string | undefined>;
  detailLevel: ChipDetailLevel;
  onAddClick?: (date: Date, weekIndex: number, weekdayIndex: number) => void;
  onChipClick?: (pub: ContentPublication) => void;
  onDrop?: (payload: DayCellDropPayload) => void;
  /** H.2 — fired when a Topic card from TopicBacklogSidebar is dropped. */
  onTopicDrop?: (payload: DayCellTopicDropPayload) => void;
}

export const ContentCalendarGrid: React.FC<ContentCalendarGridProps> = ({
  windowStart,
  weekCount,
  publications,
  colorsByStrategy,
  topicTitlesByPub = {},
  detailLevel,
  onAddClick,
  onChipClick,
  onDrop,
  onTopicDrop,
}) => {
  const weeks: CalendarWeek[] = useMemo(
    () => buildWeeks(windowStart, weekCount),
    [windowStart, weekCount],
  );

  // Bucket publications into day cells keyed by YYYY-MM-DD.
  const pubsByDay = useMemo(() => {
    const acc: Record<string, ContentPublication[]> = {};
    for (const p of publications) {
      const key = dayKey(p.scheduledAt);
      (acc[key] ??= []).push(p);
    }
    // Sort within cell by scheduledAt for stable chip order.
    for (const k of Object.keys(acc)) {
      acc[k].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
    }
    return acc;
  }, [publications]);

  const todayKey = useMemo(() => dayKey(new Date()), []);

  return (
    <Box
      data-testid={CONTENT_PLAN.CALENDAR_GRID}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(96px, 112px) repeat(7, minmax(112px, 1fr))',
          md: 'minmax(104px, 120px) repeat(7, minmax(0, 1fr))',
        },
        minWidth: { xs: 960, md: 0 },
        width: '100%',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      {/* Header row: empty corner + 7 weekday labels */}
      <Box sx={{ p: 1, minWidth: 0, borderRight: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>Week</Typography>
      </Box>
      {WEEKDAY_LABELS.map((lbl) => (
        <Box
          key={lbl}
          sx={{ p: 1, minWidth: 0, borderRight: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover' }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600 }}>{lbl}</Typography>
        </Box>
      ))}

      {/* Weeks */}
      {weeks.map((week) => (
        <React.Fragment key={week.index}>
          <Box
            data-testid={CONTENT_PLAN.CALENDAR_WEEK_ROW}
            data-week-index={week.index}
            sx={{ p: 1, minWidth: 0, borderRight: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default' }}
          >
            <Typography variant="caption" sx={{ fontWeight: 500, display: 'block' }}>{week.label}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>Week {week.index + 1}</Typography>
          </Box>

          {daysOfWeek(week).map((day) => {
            const pubs = pubsByDay[day.key] ?? [];
            return (
              <DayCell
                key={day.key}
                date={day.date}
                dateKey={day.key}
                weekIndex={week.index}
                weekdayIndex={day.weekdayIndex}
                isToday={day.key === todayKey}
                publications={pubs}
                colorsByStrategy={colorsByStrategy}
                topicTitlesByPub={topicTitlesByPub}
                detailLevel={detailLevel}
                onAddClick={onAddClick}
                onChipClick={onChipClick}
                onDrop={onDrop}
                onTopicDrop={onTopicDrop}
              />
            );
          })}
        </React.Fragment>
      ))}
    </Box>
  );
};
